import { UrlBuilder } from "@synvox/api";
import { useState } from "react";
import {
  AutoSizer,
  InfiniteLoader,
  List as VirtualizedList,
} from "react-virtualized";
import styled, { css, keyframes } from "styled-components";
import { useSkipioApi, useStargateApi } from "../api";
import usePages, { PaginatingEndpoint } from "../hooks/use-pages";
import { BORDER_RADIUS, THEME_COLOR, THEME_COLOR_DARK } from "../vars";
import Checkbox from "./Checkbox";
import { OpenFolder } from "./Icons";
import Title from "./Title";

function filterObjectValues(obj: Record<string, any>) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v));
}

function getValueFromKey(key: string, object: any) {
  if (!object) return null;
  const keys = key.split(".");
  let tempVal: any;
  keys.forEach((k) => {
    if (tempVal !== null) tempVal = tempVal ? tempVal[k] : object[k];
  });
  return tempVal;
}

function List({
  endpoint,
  params = {},
  onRowClick,
  sectionHeaderKey,
  isSelected = () => false,
  renderTitle,
  renderSubTitle,
}: {
  endpoint: ({
    skipioApi,
    stargateApi,
  }: {
    skipioApi: UrlBuilder<any>;
    stargateApi: UrlBuilder<any>;
  }) => PaginatingEndpoint<any>;
  params?: Record<string, any>;
  onRowClick?: (row: any) => void;
  sectionHeaderKey?: string;
  isSelected?: (row: any) => boolean;
  renderTitle: (row: any) => void;
  renderSubTitle?: (row: any) => void;
}) {
  const skipioApi = useSkipioApi();
  const stargateApi = useStargateApi();
  const [page, setPage] = useState(1);
  const { data, loading, totalCount } = usePages(
    endpoint({ skipioApi, stargateApi }),
    filterObjectValues({
      ...params,
    }),
    page
  );

  const sortedData: any[] = [];
  if (sectionHeaderKey) {
    data.forEach((datum) => {
      if (
        getValueFromKey(sectionHeaderKey, datum) &&
        sortedData.findIndex(
          (d) =>
            d.isHeader && d.name === getValueFromKey(sectionHeaderKey, datum)
        ) < 0
      ) {
        sortedData.push({
          isHeader: true,
          name: getValueFromKey(sectionHeaderKey, datum),
        });
      }
      sortedData.push(datum);
    });
  } else {
    data.forEach((datum) => sortedData.push(datum));
  }

  return (
    <Container>
      <div
        style={{
          position: "absolute",
          bottom: "40px",
          right: "40px",
          zIndex: 100,
        }}
      >
        <LoaderCircle style={{ opacity: loading ? 1 : 0 }} />
      </div>

      <InfiniteLoader
        isRowLoaded={({ index }) => Boolean(data[index])}
        loadMoreRows={async () => {
          if (loading) return;
          setPage(page + 1);
        }}
        rowCount={totalCount}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ width, height }) => (
              <VirtualizedList
                ref={registerChild}
                height={height}
                rowHeight={50}
                rowCount={sortedData.length}
                onRowsRendered={onRowsRendered}
                width={width}
                rowRenderer={({ index, key, style }) => {
                  const row = sortedData[index];
                  return (
                    <div style={style} key={key}>
                      {row.isHeader ? (
                        <SectionHeader index={index}>
                          {sectionHeaderKey && <OpenFolder />}
                          <Title>{row.name}</Title>
                        </SectionHeader>
                      ) : (
                        <ListItem
                          index={index}
                          onClick={() =>
                            onRowClick && onRowClick({ rowData: row })
                          }
                          inFolder={
                            sectionHeaderKey &&
                            Boolean(getValueFromKey(sectionHeaderKey, row))
                              ? true
                              : false
                          }
                          isSelected={isSelected(row)}
                        >
                          {renderTitle(row)}
                          {!!renderSubTitle && renderSubTitle(row)}
                        </ListItem>
                      )}
                    </div>
                  );
                }}
              />
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </Container>
  );
}

export default List;

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: 0px;
  position: relative;
  .ReactVirtualized__Table,
  .ReactVirtualized__List {
    position: absolute;
    box-shadow: 0px 0px 0px 1px #dcdcdc inset;
    overflow: hidden;
    border-radius: ${BORDER_RADIUS};
    background: white;
  }
  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    display: flex;
    align-items: center;

    padding: 0px 10px;
    &:not(:first-child) {
      border-left: 1px solid rgba(0, 0, 0, 0.05);
    }
  }
  .ReactVirtualized__Table__headerRow {
    background: ${THEME_COLOR};
    box-shadow: 0 0 0 1px ${THEME_COLOR_DARK} inset;
    color: white;
    text-transform: none;
    font-weight: 500;
  }
  .ReactVirtualized__Table__row {
    cursor: pointer;
    box-shadow: 0px 0px 0px 1px transparent;
    transition: background 0.2s;
    user-select: none;
    &.even {
    }
    &.odd {
      background: rgba(0, 0, 0, 0.01);
    }
    &.selected {
      background: ${THEME_COLOR}11;
    }
    &:hover:not(.selected) {
      background: rgba(0, 0, 0, 0.04);
    }
    &:active:not(.selected) {
      background: rgba(0, 0, 0, 0.06);
    }
  }
  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    display: flex;
    align-items: center;
    white-space: normal;
    padding: 0px 10px;
    &:not(:first-child) {
      border-left: 1px solid rgba(0, 0, 0, 0.05);
    }
  }
  .ReactVirtualized__Table__sortableHeaderColumn {
  }
  .ReactVirtualized__Table__sortableHeaderIcon {
  }
  ${Checkbox} {
    top: -10px;
  }
`;

const loaderKeyframes = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LoaderCircle = styled.div`
  display: block;
  border-radius: 10000px;
  height: 40px;
  width: 40px;
  border: 4px solid transparent;
  border-left-color: ${THEME_COLOR};
  animation: 1s ${loaderKeyframes} linear infinite;
  transition-duration: 0.2s;
`;

const ListItem = styled.div<{
  index?: number;
  inFolder?: boolean;
  isSelected?: boolean;
}>`
  height: 50px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
  border-bottom: 1px solid #dcdcdc;
  border-right: 1px solid #dcdcdc;
  border-left: 1px solid #dcdcdc;
  background-color: #ffffff;
  ${(p) =>
    p.index === 0 &&
    !p.inFolder &&
    css`
      border-top: 1px solid #dcdcdc;
      border-top-right-radius: ${BORDER_RADIUS};
      border-top-left-radius: ${BORDER_RADIUS};
    `}
  ${(p) =>
    p.inFolder &&
    css`
      padding-left: 30px;
    `}
  ${(p) =>
    p.isSelected &&
    css`
      background-color: #f1f7f9;
    `}
  &:hover {
    background-color: #f5f5f5;
  }
`;

const SectionHeader = styled(ListItem)<{ index?: number }>`
  justify-content: flex-start;
  background-color: #fcfcfc;
  cursor: default;
  ${(p) =>
    p.index === 0 &&
    css`
      border-top: 1px solid #dcdcdc;
      border-top-right-radius: ${BORDER_RADIUS};
      border-top-left-radius: ${BORDER_RADIUS};
    `}
  &:hover {
    background-color: #fcfcfc;
  }
  ${Title} {
    margin-left: 5px;
  }
`;
